import { useMemo } from 'react';
import { useFieldDefaults } from './defaults';
import { fieldType } from './registry';
import { resolveField } from './resolve';
import type { FieldDef, FieldValues, ResolvedField } from './types';

const NO_VALUES: FieldValues = {};

export interface RenderFieldProps {
  field: ResolvedField<unknown>;
  value: unknown;
  onChange: (value: unknown) => void;
  onBlur?: () => void;
  error?: string;
  values: FieldValues;
}

/** The registered component for an already-resolved field. */
export function RenderField({ field, value, onChange, onBlur, error, values }: RenderFieldProps) {
  const { component: Component } = fieldType(field.type);
  return (
    <Component field={field} value={value === undefined ? field.defaultValue : value} onChange={onChange}
               onBlur={onBlur} error={error} values={values} />
  );
}

export interface FormFieldProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: FieldDef<any>;
  value: unknown;
  onChange: (value: unknown) => void;
  onBlur?: () => void;
  error?: string;
  /** The whole form's values, for fields whose settings depend on other fields. */
  values?: FieldValues;
}

/** The one entry point for rendering a field — the counterpart of uKnowvaUI::loadFormField. */
export function FormField({ field, value, onChange, onBlur, error, values = NO_VALUES }: FormFieldProps) {
  const defaults = useFieldDefaults();
  const resolved = useMemo(() => resolveField(field, defaults, values), [field, defaults, values]);
  if (resolved.hidden) return null;
  return <RenderField field={resolved} value={value} onChange={onChange} onBlur={onBlur} error={error} values={values} />;
}
